import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as Dialog from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import {
  LogOut,
  Sun,
  Moon,
  Monitor,
  Search as SearchIcon,
  Target,
  Bug,
  Fingerprint,
  Zap,
  ExternalLink,
  Loader2,
  RotateCw,
} from 'lucide-react';
import useKeyboardShortcuts, { prettyShortcut } from '@/hooks/useKeyboardShortcuts';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import SeverityBadge from '@/components/primitives/SeverityBadge';
import { CUSTOMER_ITEMS } from '@/components/layout/nav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CACHE_TTL_MS = 2 * 60_000;
const IDLE_BRIEFS = 8;     // shown before the user types
const MAX_MATCHES = 50;    // per group while searching

const CommandPaletteContext = createContext({
  open: () => {},
  close: () => {},
  isOpen: false,
});

export const useCommandPalette = () => useContext(CommandPaletteContext);

/* ============================================================
   Provider — wires ⌘K / Ctrl+K globally and exposes open()/close().
   ============================================================ */
export function CommandPaletteProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  // The palette has several launchers (topbar, shortcut, page buttons), so there is
  // no Radix Trigger; remember what had focus and hand it back on close.
  const returnFocusRef = useRef(null);
  const remember = () => {
    const el = document.activeElement;
    returnFocusRef.current = el && el !== document.body ? el : null;
  };
  const open = useCallback(() => { remember(); setIsOpen(true); }, []);
  const close = useCallback(() => setIsOpen(false), []);

  useKeyboardShortcuts({
    '!mod+k': () => setIsOpen((v) => { if (!v) remember(); return !v; }),
  });

  const value = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="is-dialog-overlay" />
          <Dialog.Content
            className="is-palette"
            data-testid="command-palette"
            aria-modal="true"
            onCloseAutoFocus={(e) => {
              const el = returnFocusRef.current;
              if (el && el.isConnected) {
                e.preventDefault();
                el.focus();
              }
            }}
          >
            <Dialog.Title className="sr-only">Search and commands</Dialog.Title>
            <Dialog.Description className="sr-only">
              {localStorage.getItem('token')
                ? 'Type to search briefs and CVEs or filter commands. Use the arrow keys to move and Enter to run.'
                : 'Type to filter commands. Use the arrow keys to move and Enter to run.'}
            </Dialog.Description>
            {isOpen && <CommandPalette onClose={close} />}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </CommandPaletteContext.Provider>
  );
}

/* ============================================================
   Workspace search data — cached between opens so the palette
   opens instantly and doesn't refetch the whole history each time.
   ============================================================ */
let cache = { at: 0, token: null, briefs: [], cves: [], actors: [] };

function useWorkspaceIndex() {
  const token = localStorage.getItem('token');
  const fresh = cache.token === token && Date.now() - cache.at < CACHE_TTL_MS;
  const [state, setState] = useState(() => ({
    briefs: fresh ? cache.briefs : [],
    cves: fresh ? cache.cves : [],
    actors: fresh ? cache.actors || [] : [],
    loading: Boolean(token) && !fresh,
    error: null,
  }));

  const load = useCallback(async () => {
    if (!token) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [a, c, ac] = await Promise.all([
        axios.get(`${API}/dashboard/attacks?severity=all`, { headers }),
        axios.get(`${API}/dashboard/cves`, { headers }),
        axios.get(`${API}/actors/index`, { headers }).catch(() => ({ data: { actors: [] } })),
      ]);
      const briefs = Array.isArray(a.data) ? a.data : [];
      const cves = c.data?.cves || [];
      const actors = ac.data?.actors || [];
      cache = { at: Date.now(), token, briefs, cves, actors };
      setState({ briefs, cves, actors, loading: false, error: null });
    } catch (error) {
      setState((s) => ({ ...s, loading: false, error }));
    }
  }, [token]);

  useEffect(() => {
    if (token && !fresh) load();
  }, []);

  return { ...state, reload: load, enabled: Boolean(token) };
}

const matches = (q, ...fields) => fields.some((f) => f && String(f).toLowerCase().includes(q));

/* ============================================================
   Command Palette UI (cmdk: combobox + listbox semantics,
   aria-activedescendant, arrow keys; Radix Dialog: focus trap,
   Escape, focus return)
   ============================================================ */
function CommandPalette({ onClose }) {
  const navigate = useNavigate();
  const { theme, preference, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const [query, setQuery] = useState('');
  const index = useWorkspaceIndex();
  const q = query.trim().toLowerCase();

  const commands = useMemo(() => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    return [
      ...(user ? CUSTOMER_ITEMS.map((i) => ({ id: `nav:${i.to}`, group: 'Navigate', icon: i.icon, label: i.label, keys: i.keys ? `g ${i.keys}` : undefined, action: () => navigate(i.to) })) : []),
      { id: 'pref:theme', group: 'Preferences', icon: nextTheme === 'light' ? Sun : Moon, label: `Switch to ${nextTheme} theme`, action: () => setTheme(nextTheme) },
      preference !== 'system' && { id: 'pref:system', group: 'Preferences', icon: Monitor, label: 'Match system theme', action: () => setTheme('system') },
      user && { id: 'act:logout', group: 'Account', icon: LogOut, label: 'Sign out', action: () => { logout(); navigate('/login'); } },
    ].filter(Boolean);
  }, [user, theme, preference, setTheme, logout, navigate]);

  const groups = useMemo(() => {
    const cmdHits = q ? commands.filter((c) => matches(q, c.label, c.group, c.keys)) : commands;

    const briefList = q
      ? index.briefs.filter((b) => matches(q, b.name, b.threat_actor, b.severity, ...(b.cves || []))).slice(0, MAX_MATCHES)
      : index.briefs.slice(0, IDLE_BRIEFS);
    const briefHits = briefList.map((b) => ({
      id: `brief:${b.attack_id || b.id}`,
      icon: Target,
      label: b.name,
      sub: b.threat_actor || '',
      severity: b.severity,
      action: () => navigate(`/threats?id=${encodeURIComponent(b.attack_id || b.id)}`),
    }));

    const cveHits = (q ? index.cves.filter((c) => matches(q, c.cve_id, ...(c.related_threats || []).map((t) => t.name))) : [])
      .slice(0, MAX_MATCHES)
      .map((c) => {
        const related = (c.related_threats || []).find((t) => t.attack_id);
        return {
          id: `cve:${c.cve_id}`,
          icon: Bug,
          label: c.cve_id,
          severity: c.max_severity,
          // Open the brief that references it in-app; NVD only when no brief is linked.
          sub: related ? related.name : 'Opens NVD',
          external: !related,
          action: related
            ? () => navigate(`/threats?id=${encodeURIComponent(related.attack_id)}`)
            : () => window.open(`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(c.cve_id)}`, '_blank', 'noopener,noreferrer'),
        };
      });

    const actorHits = (q ? index.actors.filter((a) => matches(q, a.name, a.mitre_id, ...a.aliases)) : [])
      .slice(0, MAX_MATCHES)
      .map((a) => ({
        id: `actor:${a.slug}`,
        icon: Fingerprint,
        label: a.name,
        sub: [a.type_label, a.aliases.find((x) => x.toLowerCase().includes(q)) ? `also ${a.aliases.find((x) => x.toLowerCase().includes(q))}` : a.mitre_id].filter(Boolean).join(' · '),
        action: () => navigate(`/actors/${a.slug}`),
      }));

    const out = [];
    const byGroup = {};
    cmdHits.forEach((c) => { (byGroup[c.group] = byGroup[c.group] || []).push(c); });
    if (byGroup.Navigate) out.push({ heading: 'Navigate', items: byGroup.Navigate });
    if (briefHits.length) out.push({ heading: q ? 'Briefs' : 'Latest briefs', items: briefHits });
    if (actorHits.length) out.push({ heading: 'Threat actors', items: actorHits });
    if (cveHits.length) out.push({ heading: 'CVEs', items: cveHits });
    ['Preferences', 'Account'].forEach((g) => { if (byGroup[g]) out.push({ heading: g, items: byGroup[g] }); });
    return out;
  }, [q, commands, index.briefs, index.cves, index.actors, navigate]);

  const total = groups.reduce((n, g) => n + g.items.length, 0);

  const run = (cmd) => {
    onClose();
    // Let the dialog close (and return focus) before navigating.
    setTimeout(() => cmd.action(), 0);
  };

  return (
    <Command label="Search and commands" shouldFilter={false} loop className="is-cmdk">
      <div className="is-cmdk-input">
        <SearchIcon size={16} aria-hidden="true" />
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder={index.enabled ? 'Search briefs, CVEs and commands' : 'Go to or run a command'}
          aria-label={index.enabled ? 'Search briefs, CVEs and commands' : 'Go to or run a command'}
          data-testid="command-palette-input"
        />
        {index.loading && <Loader2 size={14} className="is-spinner" aria-label="Loading workspace" />}
        <kbd className="is-kbd is-hide-sm" aria-hidden="true">Esc</kbd>
      </div>

      <Command.List className="is-cmdk-list" aria-label="Results">
        {index.error && (
          <div className="is-cmdk-note is-cmdk-note--error" role="alert">
            <span>Couldn't load your briefs and CVEs. Commands still work.</span>
            <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={index.reload}>
              <RotateCw aria-hidden="true" /> Retry
            </button>
          </div>
        )}
        <Command.Empty className="is-cmdk-empty">
          {index.loading ? 'Searching…' : `No matches for “${query.trim()}”.`}
        </Command.Empty>
        {groups.map((g) => (
          <Command.Group key={g.heading} heading={g.heading} className="is-cmdk-group">
            {g.items.map((cmd) => {
              const Icon = cmd.icon || Zap;
              return (
                <Command.Item
                  key={cmd.id}
                  value={cmd.id}
                  onSelect={() => run(cmd)}
                  className="is-cmdk-item"
                  data-testid={`command-palette-item-${cmd.id}`}
                >
                  <Icon size={15} className="is-cmdk-icon" aria-hidden="true" />
                  <span className="is-cmdk-label">{cmd.label}</span>
                  {cmd.sub && <span className="is-cmdk-sub is-hide-sm">{cmd.sub}</span>}
                  {cmd.external && <ExternalLink size={12} className="is-cmdk-icon" aria-label="opens in a new tab" />}
                  {cmd.severity && <SeverityBadge severity={cmd.severity} short size="sm" />}
                  {cmd.keys && <kbd className="is-kbd is-hide-sm" aria-label={`Shortcut ${cmd.keys}`}>{cmd.keys}</kbd>}
                </Command.Item>
              );
            })}
          </Command.Group>
        ))}
      </Command.List>

      <div className="is-cmdk-foot">
        <span className="is-hide-sm">↑↓ move · Enter run · Esc close · {prettyShortcut('mod+k')} toggle</span>
        <span style={{ marginLeft: 'auto' }} aria-live="polite">
          {`${total} result${total === 1 ? '' : 's'}`}
          {index.enabled && !index.loading && !index.error && (
            <span className="is-hide-sm">{` · in ${index.briefs.length} briefs, ${index.cves.length} CVEs`}</span>
          )}
        </span>
      </div>
    </Command>
  );
}
